/**
 * Date utility functions for timezone-aware date calculations
 *
 * These utilities ensure that date filters use the client's local timezone
 * rather than the server's timezone, preventing issues where users in different
 * timezones see incorrect data (e.g., "today" showing zero data when the server
 * is in a different timezone and has already moved to the next day).
 */

export interface DateRange {
  startDate: string;
  endDate: string;
}

/**
 * Convert a filter type to explicit date range using the client's local timezone
 *
 * @param filterType - The type of date filter (today, past7days, etc.)
 * @returns Object with startDate and endDate in YYYY-MM-DD format
 *
 * @example
 * // User in India at 9 PM IST on Oct 8, 2025
 * convertFilterToDateRange('today')
 * // Returns: { startDate: '2025-10-08', endDate: '2025-10-08' }
 *
 * // Even though AWS server might be in US East (Oct 9),
 * // we use the user's local date (Oct 8)
 */
export const convertFilterToDateRange = (
  filterType: 'today' | 'past7days' | 'past90days' | 'monthToDate' | 'yearToDate'
): DateRange => {
  const now = new Date();
  let start: Date;
  let end: Date;

  switch (filterType) {
    case 'today':
      // Today in user's timezone
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case 'past7days':
      // Last 7 days including today
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case 'past90days':
      // Last 90 days including today
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 89);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case 'monthToDate':
      // First day of current month to today
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case 'yearToDate':
      // January 1st of current year to today
      start = new Date(now.getFullYear(), 0, 1);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    default:
      // Fallback to month to date
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
  }

  // Format dates as YYYY-MM-DD
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  return {
    startDate: formatDate(start),
    endDate: formatDate(end),
  };
};
